<template>
  <header class="app-header" :class="[
      {'app-header--active': showMobileMenu},
      `app-header--${headerTheme}`
    ]">
    <!-- Logo部分（PC和Mobile共用） -->
    <div class="app-header__logo">
      <a :href="homeLink" class="app-header__logo-link">
        <img :src="domainConfig?.['siteIcon']" class="app-header__logo-icon"
          :class="`app-header__logo-icon--${headerTheme}`" alt="">
        <div class="app-header__logo-text" :class="[
            {'app-header__logo-text--active': showMobileMenu},
            `app-header__logo-text--${headerTheme}`
          ]">
          {{domainConfig?.['siteName']}}</div>
      </a>
    </div>

    <!-- PC导航菜单 (只在桌面设备显示) -->
    <nav class="app-header__nav" v-if="deviceType === 'desktop' || deviceType === 'pc'">
      <a v-for="(item, index) in navItems" :key="index" :href="getNavLink(item.link)"
        class="app-header__nav-item" :class="[
          {'app-header__nav-item--active': isRouteActive(item.link)},
          `app-header__nav-item--${headerTheme}`
        ]">
        {{ item.name }}
      </a>
    </nav>

    <!-- Mobile菜单按钮 (只在移动设备显示) -->
    <div class="app-header__mobile-trigger" @click="toggleMobileMenu">
      <img v-if="!showMobileMenu" :src="menuSrc" alt="">
      <img v-else src="@/assets/clos-menu.svg" alt="">
    </div>

    <!-- Mobile菜单 (显示/隐藏取决于showMobileMenu) -->
    <div class="app-header__mobile-menu" v-if="showMobileMenu">
      <div class="app-header__mobile-menu-items">
        <a v-for="(item, index) in navItems" :key="index" :href="getNavLink(item.link)"
          class="app-header__mobile-menu-item" :class="[
            {'app-header__mobile-menu-item--active': isRouteActive(item.link)},
            `app-header__mobile-menu-item--${headerTheme}`
          ]" @click="showMobileMenu = false">
          {{ item.name }}
          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="10" viewBox="0 0 7 10"
            fill="none">
            <path d="M1.54175 1.5L5.04175 5L1.54175 8.5" stroke="#050B20" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
      </div>
    </div>
  </header>
</template>

<script>
import { mapGetters } from 'vuex';
import navigationMixin from '@/mixin/navigationMixin.js';

export default {
  name: 'AppHeader',
  mixins: [navigationMixin],
  props: {
    headerTheme: {
      type: String,
      default: 'light' // 可选值：'light'或'dark'
    },
  },
  computed: {
    ...mapGetters({
      deviceType: 'deviceType',
      domainConfig: 'domainConfig'
    }),
    homeLink () {
      return this.getRouteUrl('home');
    },
    menuSrc () {
      return this.headerTheme === 'dark'
        ? require('@/assets/mobile-menu-dark.svg')
        : require('@/assets/mobile-menu.svg');
    }
  },
  data () {
    return {
      showMobileMenu: false,
      navItems: [
        { name: 'Home', link: 'home' },
        { name: 'All Cars', link: 'all' },
        { name: 'Rankings', link: 'rankings' },
        { name: 'About Us', link: 'about' },
      ]
    };
  },
  methods: {
    toggleMobileMenu () {
      this.showMobileMenu = !this.showMobileMenu;

      // 如果打开菜单，禁止body滚动
      if (this.showMobileMenu) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },

    // 判断当前路由是否激活
    isRouteActive (link) {
      // 获取不包含channel参数的当前路径
      let currentPath = this.$route.path;
      const channel = this.$route.params.channel;

      // 如果存在channel参数，移除channel部分以便正确比较
      if (channel && currentPath.startsWith(`/${channel}`)) {
        currentPath = currentPath.replace(`/${channel}`, '') || '/';
      }

      // 处理根路径特殊情况
      if (link === '/' && (currentPath === '/' || currentPath === '')) {
        return true;
      }

      // 其他情况检查路径是否匹配（排除根路径情况）
      if (link !== '/') {
        return currentPath.startsWith(link);
      }

      return false;
    },

    // 获取导航链接
    getNavLink (link) {
      return this.getRouteUrl(link);
    }
  },
  beforeDestroy () {
    // 确保在组件销毁时恢复body滚动
    document.body.style.overflow = '';
  },
  mounted () {
    // 监听路由变化，关闭移动菜单
    this.$router.afterEach(() => {
      if (this.showMobileMenu) {
        this.showMobileMenu = false;
        document.body.style.overflow = '';
      }
    });
  }
};
</script>

<style lang="scss">
@import '~@/css/variables.scss';

// 使用命名空间避免样式冲突
.app-header {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	z-index: 100;
	display: flex;
	align-items: center;
	justify-content: space-between;
	box-sizing: border-box;
	height: rem(70);
	padding: 0 rem(120);
	transition: all 0.15s ease;
	border-bottom: 1px solid rgba(#fff, 0.04);

	&--light {
		background: transparent;
	}

	&--dark {
		background: #fff;
		box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08);
	}

	&__logo {
		display: flex;
		align-items: center;
		cursor: pointer;
		gap: rem(10);
		z-index: 100; // 确保在移动菜单上方
		flex: 1;
	}

	&__logo-link {
		display: flex;
		align-items: center;
		gap: rem(10);
		text-decoration: none;
	}

	&__logo-icon {
		width: rem(36);
		height: rem(36);
		transition: all 0.3s ease;

		&--light {
			filter: brightness(0) invert(1); // 在light主题下使用滤镜使logo变为白色
		}

		&--dark {
			filter: none; // 在dark主题下显示原始颜色
		}
	}

	&__logo-text {
		font-size: rem(18);
		font-style: normal;
		font-weight: 900;
		line-height: rem(18); /* 100% */
		text-transform: uppercase;
		transition: color 0.3s ease;

		&--light {
			color: #ffffff !important;
		}

		&--dark {
			color: #050b20 !important;
		}

		&--active {
			color: #050b20 !important;
		}
	}

	&__nav {
		display: flex;
		gap: rem(50);
	}

	&__nav-item {
		font-size: rem(14);
		font-weight: 400;
		text-decoration: none;
		transition: color 0.3s ease;
		position: relative;
		padding-bottom: rem(5);

		&--light {
			color: #ffffff !important;
			&.app-header__nav-item--active:after {
				background: #fff !important;
			}
		}

		&--dark {
			color: #050b20 !important;
			&.app-header__nav-item--active:after {
				background: #405ff2 !important;
			}
		}
	}

	&__mobile-trigger {
		width: rem(24);
		height: rem(24);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;

		img {
			width: 100%;
			height: 100%;
		}
	}

	&__mobile-menu {
		position: fixed;
		top: rem(70);
		left: 0;
		width: 100%;
		height: calc(100vh - rem(70));
		background: #ffffff;
		padding: rem(20);
		overflow-y: auto;
	}

	&__mobile-menu-items {
		display: flex;
		flex-direction: column;
		gap: rem(20);
	}

	&__mobile-menu-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-size: rem(16);
		font-weight: 500;
		color: #050b20;
		text-decoration: none;
		padding: rem(16) 0;
		border-bottom: 1px solid #e9e9e9;

		svg {
			width: rem(7);
			height: rem(10);
		}

		&--active {
			color: #405ff2 !important;
		}
	}
}

// 响应式样式 - 使用媒体查询保证始终生效
@media screen and (max-width: 768px) {
	.app-header {
		&__nav {
			display: none !important;
		}

		&__mobile-trigger {
			display: block !important;
		}
	}
}

@media screen and (min-width: 769px) {
	.app-header {
		&__mobile-trigger {
			display: none !important;
		}

		&__nav {
			display: flex !important;
		}
	}
}

// 设备类型适配
.detect_deviece_pc,
.detect_deviece_desktop {
	.app-header {
		&__nav-item {
			height: rem(70);
			line-height: rem(70);
			&--active {
				&:after {
					content: '';
					position: absolute;
					bottom: 0;
					left: 50%;
					transform: translateX(-50%);
					width: rem(24);
					height: rem(2);
					border-radius: rem(1);
					transition: background 0.3s ease;
				}
			}
		}
	}
}

.detect_deviece_mobile {
	.app-header {
		padding: rem(18) rem(16) !important;
		height: rem(72) !important;

		&--active {
			background: #fff !important;
		}

		&__nav-item--active:after {
			bottom: rem(-16) !important; /* 调整为移动设备下header底部位置 */
		}

		&__logo-text {
			font-size: rem(18);
			line-height: rem(18);
			font-weight: 900;
			text-transform: uppercase;
			position: relative;
			overflow: hidden;
			display: -webkit-box;
			text-overflow: ellipsis;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			&--active {
				color: #050b20 !important;
			}
		}
	}
}
</style>
